import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { TextField } from '@ovComponents/2-component/textField/textField';
import type { PdfSection } from '@utils/downloadHtmlAsPdf';
import { Alert, Box, Button } from 'hephaestus/web';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

export const CalculatorPdfPreviewDialog = ({
  error,
  filename,
  isSaving,
  onClose,
  onFilenameChange,
  onSave,
  open,
  sections,
}: {
  error: boolean;
  filename: string;
  isSaving: boolean;
  onClose: () => void;
  onFilenameChange: (value: string) => void;
  onSave: () => void;
  open: boolean;
  sections: PdfSection[];
}) => {
  const { t } = useTranslation('workflowCompletions');

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      onClose={handleClose}
      open={open}
      data-testid='calculator-pdf-preview-dialog'
    >
      <DialogTitle onClose={isSaving ? undefined : handleClose}>
        {t('calculator.pdfPreview.title')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {t('calculator.pdfPreview.saveError')}
          </Alert>
        )}
        <TextField
          disabled={isSaving}
          fullWidth
          label={t('calculator.pdfPreview.fileName')}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onFilenameChange(event.target.value)
          }
          sx={{ mb: 2 }}
          value={filename}
          data-testid='calculator-pdf-filename-field'
        />
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: '60vh',
            overflow: 'auto',
            p: 2,
          }}
        >
          {sections.map((section, index) => (
            <Box
              key={index}
              sx={{
                mb: 1.5,
                ...(section.startsNewPage && {
                  borderTop: 2,
                  borderColor: 'divider',
                  mt: 2,
                  pt: 2,
                }),
              }}
            >
              <Box
                component='img'
                src={section.dataUrl}
                sx={{ display: 'block', height: 'auto', width: '100%' }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Button disabled={isSaving} onClick={handleClose} variant='outlined'>
          {t('calculator.pdfPreview.cancel')}
        </Button>
        <Button
          disabled={!filename.trim() || isSaving}
          loading={isSaving}
          onClick={onSave}
          data-testid='calculator-pdf-save-button'
        >
          {t('calculator.pdfPreview.save')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
